/* ==========================================================================
   Vinhuset × Rebuy: recommendations from Rebuy's API, shown with the theme's own cards.
   Rebuy's own script (Smart Cart, widgets) is not loaded (Theme settings → Vinhuset: Kurv → "Bloker Rebuy-script").

   1. <vh-rebuy-cart> – cart drawer and cart page. One group per Rebuy widget (Theme settings → Vinhuset: Rebuy), in
      that order, with Rebuy's title (also a title set by a rule in Rebuy) and the theme's cart rows
      (/products/<handle>?section_id=vh-api-cart-upsell). Follows the cart itself: Broadcast rebuilds the drawer's lines
      on every change, but never this element. Rebuy (and the cards) are asked before the tap where the cart to come is
      known: on a product page for its product, elsewhere when the pointer rests on "Læg i kurv" or a finger touches it.
      At the tap the drawer shows the placeholder (or keeps the lists it has, without the product added) and draws the
      new cart's lists as soon as they are ready, before Shopify has answered the add (which checks them). Once a widget
      has products, the others get 0.4 s more; a slow answer is kept for the next time. Rebuy's first answer to a cart
      it hasn't seen takes 2–4 s: lists from before wait for it up to 8 s, the placeholder up to the cart wait (Theme
      settings → Vinhuset: Rebuy, 8 s unless set), counted from the tap. The theme's own list is shown when no widget has
      anything by then.
   2. <vh-rebuy-rail> – section "Rebuy-anbefalinger" on product, collection, landing and home pages: the chosen widget,
      else one of Rebuy's own lists (its suggestions, top sellers, recently viewed, bought before), with the theme's
      product cards (/products/<handle>?section_id=vh-api-rebuy-card), loaded when the row nears the screen. Rebuy gets
      the page's product or collection, as its own widgets do.
   3. Attribution, as Rebuy's own widgets do it: an add from a recommendation carries the hidden line properties
      _source ("Rebuy"), _attribution (the widget type, e.g. "Rebuy Cart") and _widget_id. Rebuy credits the sale from
      the order. A recommendation's link carries Rebuy's _rdiscovery parameters, so an add on that product's page within
      24 hours is credited as "Product Discovery". A widget's own tracking switches in Rebuy (source, widget,
      attribution, product discovery) are followed.
   4. Tracking, 1:1 with Rebuy's own script on the current theme (its Tracker): the same consent check, cookies and
      events (session started, product and collection viewed, widget viewed), sent to the same API. Rebuy's numbers
      therefore mean the same with both themes.

   Debugging: add ?vh_rebuy_debug=1 to any URL (kept for the session; ?vh_rebuy_debug=0 ends it). Requests and events
   are logged in the console, and no events are sent to Rebuy.
   ========================================================================== */
(function () {
  'use strict';

  const vh = (window.vh = window.vh || {});
  const cfg = (vh.config && vh.config.rebuy) || {};
  if (!cfg.key || vh.rebuy) return;

  const API = 'https://rebuyengine.com/api/v1';
  const EVENTS_API = 'https://rebuyengine.com/api/v2/analytics/event/bulk';
  const routes = (window.theme && window.theme.routes) || {};
  const ROOT = routes.root || '/';
  const CART_URL = routes.cart_url || '/cart';
  const CART_SECTION = 'vh-api-cart-upsell';
  const CARD_SECTION = 'vh-api-rebuy-card';
  const FALLBACK_SECTION = 'vh-api-cart-upsells';
  const WIDGET_GRACE = 400; // once a widget has products: how much longer the others are waited for
  const WIDGET_WAIT = 8000; // when none has products: how long the cart's lists from before wait for the new ones
  const CART_WAIT = Number(cfg.cartWait) > 0 ? Math.min(Number(cfg.cartWait), WIDGET_WAIT) : WIDGET_WAIT; // …and the placeholder
  const PREFETCH_MAX = 12; // carts asked for ahead of a tap, per page
  const hiddenTags = new Set((cfg.hiddenTags || []).concat('hide').map((tag) => String(tag).trim().toLowerCase()).filter(Boolean));
  const serviceProducts = new Set((cfg.serviceProductIds || []).map(Number));

  // Rebuy's own labels for the "type" in its sales reports (the widget's type in Rebuy)
  const ATTRIBUTION = {
    cart: 'Rebuy Cart',
    product: 'Rebuy Product',
    product_upsell: 'Rebuy Product Upsell',
    product_addon: 'Rebuy Product Add-Ons',
    dynamic_bundle: 'Rebuy Dynamic Product Bundle',
    pre_purchase: 'Rebuy Pre-Purchase Product',
    gift_with_purchase: 'Rebuy Gift with Purchase',
  };
  const DISCOVERY = 'Product Discovery';

  /* ------------------------------------------------------------------------
     Small helpers: debug log, session cache, ids
     ------------------------------------------------------------------------ */
  const DEBUG = (() => {
    try {
      if (/[?&]vh_rebuy_debug=1\b/.test(window.location.search)) sessionStorage.setItem('vh:rebuy:debug', '1');
      if (/[?&]vh_rebuy_debug=0\b/.test(window.location.search)) sessionStorage.removeItem('vh:rebuy:debug');
      return sessionStorage.getItem('vh:rebuy:debug') === '1';
    } catch (e) {
      return false;
    }
  })();
  const log = (...args) => {
    if (DEBUG) console.info('[vh-rebuy]', ...args);
  };

  // Session cache for what is the same on every page: widget settings, recommendations, card HTML
  const cache = {
    get(key, maxAge) {
      try {
        const raw = sessionStorage.getItem(key);
        if (!raw) return null;
        const entry = JSON.parse(raw);
        return Date.now() - entry.t < maxAge ? entry.v : null;
      } catch (e) {
        return null;
      }
    },
    set(key, value) {
      try {
        sessionStorage.setItem(key, JSON.stringify({t: Date.now(), v: value}));
      } catch (e) {
        /* full or blocked: the in-memory cache still works */
      }
    },
  };
  const memo = new Map();
  function once(key, make) {
    if (!memo.has(key)) {
      const promise = make().catch((error) => {
        memo.delete(key); // try again next time
        throw error;
      });
      memo.set(key, promise);
    }
    return memo.get(key);
  }

  const toIds = (list) => Array.from(new Set(list.map(Number).filter((id) => id > 0)));
  // A title from Rebuy may hold HTML from its editor; the theme shows it as text, as Rebuy does with tab titles
  const plainText = (value) => String(value || '').replace(/<[^>]*>/g, '').trim();
  const withTimeout = (promise, ms) => Promise.race([promise, new Promise((resolve) => setTimeout(() => resolve(null), ms))]);
  // Several answers (each one resolves, null when it failed): those that are in when `wait` is up, or `grace` after the
  // first one `useful` says has something, or all of them. The ones not in yet are null.
  function settle(requests, wait, grace, useful) {
    return new Promise((resolve) => {
      const results = requests.map(() => null);
      let left = requests.length;
      let timer = 0;
      let graceTimer = 0;
      const done = () => {
        clearTimeout(timer);
        clearTimeout(graceTimer);
        resolve(results.slice());
      };
      if (!left) return done();
      timer = setTimeout(done, Math.max(0, wait));
      requests.forEach((request, index) => {
        request.then((result) => {
          results[index] = result;
          left -= 1;
          if (!left) done();
          else if (!graceTimer && useful(result)) graceTimer = setTimeout(done, grace);
        });
      });
    });
  }
  const idle = (fn) => ('requestIdleCallback' in window ? window.requestIdleCallback(fn, {timeout: 3000}) : setTimeout(fn, 1500));
  let instances = 0;

  function cookie(name) {
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
  }
  function setCookie(name, value, seconds) {
    document.cookie = `${name}=${encodeURIComponent(value)}; max-age=${seconds}; path=/; SameSite=Lax`;
  }

  /* ------------------------------------------------------------------------
     Consent and visitor, exactly as Rebuy's own script (its Tracker) on the current theme:
     - Tracking when Shopify's Customer Privacy API allows analytics and marketing, and (Rebuy's rule) when that API
       isn't there at all. The API is loaded first, as Rebuy does, and a later choice is followed
       (visitorConsentCollected): yes starts tracking, no removes Rebuy's visitor and session cookies.
     - Rebuy's cookies: _ruid (visitor id, 1095 days), _rsession (session, 30 minutes) and _rutm (UTM parameters,
       30 minutes), in Rebuy's format, so a visitor keeps one id with both themes.
     ------------------------------------------------------------------------ */
  function userCanBeTracked() {
    const privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!privacy || !privacy.analyticsProcessingAllowed || !privacy.marketingAllowed) return true;
    return Boolean(privacy.analyticsProcessingAllowed() && privacy.marketingAllowed());
  }

  const encodeCookie = (value) => btoa(unescape(encodeURIComponent(JSON.stringify(value))));
  function decodeCookie(value) {
    try {
      return value ? JSON.parse(decodeURIComponent(escape(atob(value)))) : null;
    } catch (e) {
      return null;
    }
  }
  function removeCookie(name) {
    document.cookie = `${name}=; max-age=0; path=/`;
  }

  const UTM_KEYS = ['utm_campaign', 'utm_medium', 'utm_source', 'utm_term', 'utm_content'];

  const tracker = {
    identity: null,
    initDone: false,

    /* Rebuy's setupTracking: the visitor id from Rebuy's cookie, else a new visitor (which starts a session event) */
    setup() {
      if (!userCanBeTracked()) return;
      let isNew = false;
      if (!this.identity) {
        const saved = decodeCookie(cookie('_ruid'));
        if (saved && saved.uuid) {
          this.identity = {uuid: saved.uuid};
        } else {
          const uuid = window.crypto && typeof window.crypto.randomUUID === 'function' ? window.crypto.randomUUID() : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;
          this.identity = {uuid};
          isNew = true;
        }
      }
      setCookie('_ruid', encodeCookie({uuid: this.identity.uuid}), 1095 * 86400);
      this.session();
      if (isNew) track.sessionStarted();
    },

    /* Rebuy's getSession: one id per 30 minutes of activity */
    session() {
      if (!this.identity || !userCanBeTracked()) return null;
      let id = cookie('_rsession');
      if (!id) id = Array.from({length: 16}, () => Math.floor(Math.random() * 16).toString(16)).join('');
      setCookie('_rsession', id, 30 * 60);
      this.identity.session = id;
      return id;
    },

    /* Rebuy's clearTracking */
    clear() {
      this.identity = null;
      this.initDone = false;
      removeCookie('_ruid');
      removeCookie('_rsession');
    },

    uuid() {
      return this.identity && userCanBeTracked() ? this.identity.uuid : null;
    },

    /* Rebuy's manageUTMParameters: UTM parameters in the URL are kept for 30 minutes; a page without parameters
       keeps the saved ones alive */
    manageUtm() {
      let utm = null;
      if (window.location.search.length > 1) {
        const params = new URLSearchParams(window.location.search);
        UTM_KEYS.forEach((key) => {
          if (params.get(key)) utm = {...(utm || {}), [key]: params.get(key)};
        });
      } else {
        utm = decodeCookie(cookie('_rutm'));
      }
      if (utm && userCanBeTracked()) setCookie('_rutm', encodeCookie(utm), 1800);
    },
  };

  /* The page URL Rebuy's widgets send: the address with the saved UTM parameters */
  function pageUrl() {
    const url = new URL(window.location.href);
    const utm = decodeCookie(cookie('_rutm')) || {};
    Object.keys(utm).forEach((key) => {
      if (UTM_KEYS.includes(key) && !url.searchParams.has(key)) url.searchParams.set(key, utm[key]);
    });
    return url.toString();
  }

  const cartToken = () => cookie('cart') || (vh.lastCart && vh.lastCart.token) || null;

  /* ------------------------------------------------------------------------
     Rebuy API
     ------------------------------------------------------------------------ */
  function apiUrl(path, params = {}) {
    const url = new URL(API + path);
    url.searchParams.set('key', cfg.key);
    Object.keys(params).forEach((name) => {
      const value = params[name];
      if (value === undefined || value === null || value === '' || (Array.isArray(value) && !value.length)) return;
      url.searchParams.set(name, Array.isArray(value) ? value.join(',') : String(value));
    });
    return url.toString();
  }

  async function getJson(url) {
    const response = await fetch(url, {credentials: 'omit'});
    if (!response.ok) throw new Error(`Rebuy ${response.status}`);
    return response.json();
  }

  /* A widget's data source (endpoint), type, title, product limit and tracking switches, as set up in Rebuy. A switch
     is on unless Rebuy says false, as Rebuy's script reads them. */
  function widgetSettings(widgetId) {
    const key = `vh:rebuy:widget:${widgetId}`;
    return once(key, async () => {
      const cached = cache.get(key, 30 * 60 * 1000);
      if (cached) return cached;
      const json = await getJson(apiUrl('/widgets/settings', {id: widgetId}));
      const data = (json && json.data) || {};
      if (!data.endpoint) throw new Error(`Rebuy widget ${widgetId} has no data source`);
      const switches = data.tracking || {};
      const settings = {
        id: String(widgetId),
        endpoint: data.endpoint,
        type: data.type || '',
        title: plainText(data.language && data.language.title),
        limit: Number(data.limit) || 0,
        tracking: {
          source: switches.enable_source !== false,
          widget: switches.enable_widget !== false,
          attribution: switches.enable_attribution !== false,
          discovery: switches.enable_product_discovery !== false,
        },
      };
      cache.set(key, settings);
      return settings;
    });
  }

  /* A rule in Rebuy can set the title and the number of products for the visitor's cart or product */
  function ruleOverrides(metadata) {
    const overrides = {};
    const widget = (metadata && metadata.widget) || {};
    if (widget.language && plainText(widget.language.title)) overrides.title = plainText(widget.language.title);
    const rules = (metadata && Array.isArray(metadata.matched_rules) && metadata.matched_rules) || [];
    rules.forEach((rule) => {
      (rule.output || []).forEach((output) => {
        if (output.type === 'widget.language.title' && plainText(output.value) && !overrides.title) overrides.title = plainText(output.value);
        if (output.type === 'product_limit' && Number(output.value) > 0 && !overrides.limit) overrides.limit = Number(output.value);
      });
    });
    return overrides;
  }

  /* Products for a page or a cart, with the same parameters as Rebuy's own widgets (a widget on a collection page also
     gets the collection). The cache key leaves out the cart's totals, so what was asked at the tap on "Læg i kurv" is
     found again when Shopify's cart arrives. */
  function recommend(endpoint, context, limit) {
    const productIds = toIds(context.productIds || []);
    const variantIds = toIds(context.variantIds || []);
    const collectionIds = toIds(context.collectionIds || []);
    const uuid = tracker.uuid();
    const params = {
      shopify_product_ids: productIds,
      shopify_variant_ids: variantIds,
      shopify_collection_ids: collectionIds,
      limit,
      fields: 'id,handle,tags',
      url: pageUrl(),
      uuid,
      shopify_customer_id: uuid && cfg.customerId ? cfg.customerId : null,
    };
    const cart = context.cart;
    if (cart) {
      const token = cartToken();
      Object.assign(params, {
        cart_token: token,
        cart_subtotal: cart.subtotal,
        cart_count: cart.lineCount,
        cart_line_count: cart.lineCount,
        cart_item_count: cart.itemCount,
        'cart[token]': token,
        'cart[subtotal]': cart.subtotal,
        'cart[line_count]': cart.lineCount,
        'cart[item_count]': cart.itemCount,
      });
      cart.items.forEach((item, i) => {
        params[`cart[items][${i}][product_id]`] = item.product_id;
        params[`cart[items][${i}][variant_id]`] = item.variant_id;
        params[`cart[items][${i}][quantity]`] = item.quantity;
      });
    }
    const key = `vh:rebuy:recs:${endpoint}|${productIds.join(',')}|${variantIds.join(',')}|${collectionIds.join(',')}|${limit}|${uuid || ''}|${window.location.host}`;
    return once(key, async () => {
      const cached = cache.get(key, 5 * 60 * 1000);
      if (cached) return cached;
      const started = Date.now();
      const json = await getJson(apiUrl(endpoint, params));
      const result = {
        products: ((json && json.data) || []).map((p) => ({id: Number(p.id), handle: p.handle, tags: p.tags})),
        ...ruleOverrides(json && json.metadata),
      };
      log(`recommendations in ${Date.now() - started} ms`, endpoint, {productIds, variantIds}, result);
      cache.set(key, result);
      return result;
    });
  }

  /* Recommendations of one widget, with the widget's (or a rule's) title and limit */
  async function widgetRecommendations(widgetId, context, askFor) {
    const settings = await widgetSettings(widgetId);
    const result = await recommend(settings.endpoint, context, askFor);
    return {
      widgetId: settings.id,
      type: settings.type,
      title: result.title || settings.title,
      limit: result.limit || settings.limit || 0,
      tracking: settings.tracking,
      products: result.products,
    };
  }

  function usable(product, exclude) {
    if (!product || !product.handle || !(product.id > 0)) return false;
    if (serviceProducts.has(product.id) || exclude.ids.has(product.id) || exclude.handles.has(product.handle)) return false;
    const tags = Array.isArray(product.tags) ? product.tags : String(product.tags || '').split(',');
    return !tags.some((tag) => hiddenTags.has(String(tag).trim().toLowerCase()));
  }

  /* ------------------------------------------------------------------------
     The theme's own cards, rendered by Shopify (Section Rendering API)
     ------------------------------------------------------------------------ */
  function sectionInner(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const wrapper = doc.querySelector('.shopify-section') || doc.body;
    return wrapper.innerHTML.trim();
  }

  function cardHtml(handle, sectionId) {
    const key = `vh:rebuy:card:${sectionId}:${handle}`;
    return once(key, async () => {
      const cached = cache.get(key, 10 * 60 * 1000);
      if (cached !== null) return cached;
      const response = await fetch(`${ROOT}products/${encodeURIComponent(handle)}?section_id=${sectionId}`);
      // A product that no longer exists is simply skipped
      const html = response.ok ? sectionInner(await response.text()) : '';
      cache.set(key, html);
      return html;
    }).catch(() => '');
  }

  /* Plans the cards for several lists at once: each product only once, earlier lists first, at most `room` cards in
     all (the cart's lists share their room). Fetches all cards in parallel; when Shopify returns nothing for one (sold
     out, subscription only, hidden), the list's next product takes its place. */
  async function planCards(lists, sectionId, exclude, room = Infinity) {
    const used = new Set(exclude.ids);
    const plans = lists.map((list) => ({list, items: [], queue: list.products.filter((product) => usable(product, exclude))}));
    for (let round = 0; round < 3; round += 1) {
      const jobs = [];
      let free = room - plans.reduce((sum, plan) => sum + plan.items.length, 0);
      plans.forEach((plan) => {
        let missing = Math.min(plan.list.max - plan.items.length, free);
        while (missing > 0 && plan.queue.length) {
          const product = plan.queue.shift();
          if (used.has(product.id)) continue;
          used.add(product.id);
          const item = {product, html: ''};
          plan.items.push(item);
          jobs.push(cardHtml(product.handle, sectionId).then((html) => (item.html = html)));
          missing -= 1;
          free -= 1;
        }
      });
      if (!jobs.length) break;
      await Promise.all(jobs);
      plans.forEach((plan) => {
        plan.items = plan.items.filter((item) => item.html);
      });
    }
    return plans.map((plan) => ({...plan.list, items: plan.items}));
  }

  /* Ids in Shopify's HTML are the same for every copy of a card: make them unique per list */
  function uniqueIds(html, sectionId, suffix) {
    return html.split(sectionId).join(suffix);
  }

  /* ------------------------------------------------------------------------
     Attribution: the hidden line properties Rebuy reads on the order
     ------------------------------------------------------------------------ */
  function setHidden(form, name, value) {
    let input = form.querySelector(`input[name="${name}"]`);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }
    input.value = value;
  }

  /* The widget's tracking switches in Rebuy (widgetSettings) leave a property out, as Rebuy's script does */
  function attribute(form, label, widgetId, tracking = {}) {
    if (tracking.source !== false) setHidden(form, 'properties[_source]', 'Rebuy');
    if (tracking.attribution !== false) setHidden(form, 'properties[_attribution]', label);
    if (widgetId && tracking.widget !== false) setHidden(form, 'properties[_widget_id]', String(widgetId));
  }

  /* Marks a rendered list: its products, the properties on every add form in it and, for a widget, Rebuy's discovery
     parameters on the product links */
  function markList(list, items, widgetId, label, tracking = {}) {
    Array.from(list.children).forEach((el, index) => {
      const item = items[index];
      if (!item) return;
      el.setAttribute('data-vh-rebuy-product', String(item.product.id));
      el.setAttribute('data-vh-rebuy-handle', item.product.handle);
      if (widgetId && tracking.discovery !== false) {
        el.querySelectorAll('a[href*="/products/"]').forEach((link) => link.setAttribute('href', discoveryLink(link.getAttribute('href'), item.product.handle, widgetId)));
      }
    });
    list.querySelectorAll('form[action*="/cart/add"]').forEach((form) => attribute(form, label, widgetId, tracking));
  }

  /* Product Discovery, as Rebuy's widgets do it (with or without consent, as Rebuy): a recommendation's link carries
     _rdiscovery-handle and _rdiscovery-widget; the product page keeps them for 24 hours in Rebuy's own store
     (localStorage _rdiscovery) and takes them out of the address bar. */
  const DISCOVERY_KEY = '_rdiscovery';
  const DISCOVERY_PARAMS = ['_rdiscovery-handle', '_rdiscovery-widget', '_r_experimentId', '_rdiscovery-smart-search-feature', '_rdiscovery-search-term', '_rdiscovery-smart-collections'];
  const DAY = 864e5;

  function discoveries() {
    try {
      const all = JSON.parse(localStorage.getItem(DISCOVERY_KEY) || '{}');
      return all && typeof all === 'object' ? all : {};
    } catch (e) {
      return {};
    }
  }

  function saveDiscoveries(all) {
    try {
      localStorage.setItem(DISCOVERY_KEY, JSON.stringify(all));
    } catch (e) {
      /* storage blocked */
    }
  }

  function discoveryFor(handle) {
    const all = discoveries();
    const found = all[handle];
    if (!found) return null;
    if (found.timestamp > Date.now() - DAY) return found;
    delete all[handle];
    saveDiscoveries(all);
    return null;
  }

  function discoveryLink(href, handle, widgetId) {
    try {
      const url = new URL(href, window.location.origin);
      url.searchParams.set('_rdiscovery-handle', handle);
      url.searchParams.set('_rdiscovery-widget', String(widgetId));
      return url.pathname + url.search + url.hash;
    } catch (e) {
      return href;
    }
  }

  // Rebuy's manageDiscoveryProductURL, on every page
  (function manageDiscoveryUrl() {
    const params = new URLSearchParams(window.location.search);
    const handle = params.get('_rdiscovery-handle');
    const widget = params.get('_rdiscovery-widget');
    if (!handle || !widget) return;
    const all = discoveries();
    all[handle] = {widget, timestamp: Date.now()};
    if (params.get('_r_experimentId')) all[handle].experimentId = params.get('_r_experimentId');
    saveDiscoveries(all);
    DISCOVERY_PARAMS.forEach((name) => params.delete(name));
    const query = params.toString();
    window.history.replaceState({}, document.title, `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`);
    log('product discovery', handle, widget);
  })();

  /* On the product page: an add of this product gets the discovery properties. Rebuy puts them on every add form on
     the page; here only the product's own form gets them, so an add from another row isn't credited to the widget. */
  function markDiscoveryAdd(form) {
    if (!form || !cfg.productHandle || form.querySelector('input[name="properties[_source]"]')) return;
    const isMain = form.matches('[data-product-form]') || Boolean(form.closest(`[data-product-id="${cfg.productId}"]`));
    if (!isMain) return;
    const found = discoveryFor(cfg.productHandle);
    if (found && found.widget) attribute(form, DISCOVERY, found.widget);
  }
  document.addEventListener(
    'theme:cart:add',
    (event) => {
      const button = event.detail && event.detail.button;
      if (button) markDiscoveryAdd(button.form || button.closest('form'));
    },
    true
  );
  document.addEventListener('submit', (event) => markDiscoveryAdd(event.target), true);

  /* ------------------------------------------------------------------------
     Tracking, as Rebuy's trackEvent: the same events and fields, in batches to the same API. Only the event types
     Rebuy's script sends for these pages; it sends no click or add event (sales are credited from the line properties).
     ------------------------------------------------------------------------ */
  const queue = [];
  let flushTimer = null;

  function flush(keepalive) {
    clearTimeout(flushTimer);
    flushTimer = null;
    if (!queue.length) return;
    const events = queue.splice(0, queue.length);
    fetch(EVENTS_API, {
      method: 'POST',
      keepalive: Boolean(keepalive),
      credentials: 'omit',
      headers: {'Content-Type': 'application/json', 'X-Rebuy-User-Token': cfg.key},
      body: JSON.stringify(events),
    }).catch(() => {});
  }

  function themeId() {
    const attributes = window.trekkie && window.trekkie.trekkie && window.trekkie.trekkie.defaultAttributes;
    return attributes && attributes.themeId ? String(attributes.themeId) : null;
  }

  function send(event, immediate) {
    if (!cfg.tracking) return;
    const full = {subject: 'user', ...event, event_time: String(Date.now())};
    if (userCanBeTracked() && tracker.identity) {
      full.uuid = tracker.identity.uuid;
      if (tracker.identity.session) full.rsid = tracker.identity.session;
      if (cfg.customerId) full.shopify_customer_id = String(cfg.customerId);
      const token = cartToken();
      if (token) full.shopify_cart_token = token;
    }
    // Rebuy's rule: no visitor, no event
    if (!full.uuid && !full.rsid) {
      log('event not tracked (no visitor id: no consent)', event);
      return;
    }
    const theme = themeId();
    if (theme) full.theme_id = theme;
    if (DEBUG) {
      log('event (not sent in debug mode)', full);
      return;
    }
    queue.push(full);
    if (immediate || queue.length >= 10) flush(false);
    else if (!flushTimer) flushTimer = setTimeout(() => flush(false), 2000);
  }
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush(true);
  });

  const track = {
    sessionStarted() {
      const session = tracker.identity && tracker.identity.session;
      send({
        verb: 'started',
        noun: 'session',
        defined_noun_id: session ? session[0] : '-',
        meta: {
          platform: (navigator.userAgentData && navigator.userAgentData.platform) || navigator.platform || '',
          mobile: /Mobi|Android/i.test(navigator.userAgent),
          language: navigator.language,
          userAgent: navigator.userAgent,
        },
      });
    },
    productView() {
      send({verb: 'viewed', noun: 'product', shopify_product_handle: cfg.productHandle, shopify_product_id: String(cfg.productId)}, true);
    },
    collectionView() {
      send({verb: 'viewed', noun: 'collection', defined_noun_id: cfg.collectionHandle});
    },
    // When a widget is shown, as Rebuy counts it
    widgetView(widgetId) {
      if (widgetId) send({verb: 'viewed', noun: 'widget', widget_id: String(widgetId)});
    },
  };

  /* Rebuy's runInitTracking and Tracker.init: once consent is known, and again when the visitor changes it */
  function runInitTracking() {
    tracker.initDone = true;
    if (cfg.productId && cfg.productHandle) track.productView();
    if (cfg.collectionHandle) track.collectionView();
    tracker.manageUtm();
  }
  // Settled once consent is known (with or without a visitor): Rebuy's widgets start only then, so they always ask
  // with the visitor's id, which lists such as "recently viewed" and rules for a customer need
  let markTrackerReady;
  const trackerReady = new Promise((resolve) => (markTrackerReady = resolve));
  function startTracking() {
    if (userCanBeTracked()) {
      tracker.setup();
      if (!tracker.initDone) runInitTracking();
    }
    markTrackerReady();
  }
  function initTracker() {
    document.addEventListener('visitorConsentCollected', () => {
      if (userCanBeTracked()) startTracking();
      else tracker.clear();
    });
    if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      window.Shopify.loadFeatures([{name: 'consent-tracking-api', version: '0.1'}], () => startTracking());
    } else {
      startTracking();
    }
  }
  // Rebuy's script is a ScriptTag, which Shopify loads once the page has loaded, so its tracking starts then too.
  // That also keeps it clear of the store's consent script, which says "no" for a moment until Cookiebot has answered.
  if (document.readyState === 'complete') initTracker();
  else window.addEventListener('load', initTracker, {once: true});

  /* ------------------------------------------------------------------------
     Cart
     ------------------------------------------------------------------------ */
  function isService(item) {
    if (typeof vh.isServiceItem === 'function' && vh.isServiceItem(item)) return true;
    return serviceProducts.has(Number(item.product_id));
  }

  /* The cart as Rebuy needs it: products only (no fee, express or Løn lines) */
  function cartContext(raw) {
    if (!raw || !Array.isArray(raw.items)) return null;
    const items = raw.items.filter((item) => !isService(item)).map((item) => ({product_id: Number(item.product_id), variant_id: Number(item.variant_id), quantity: Number(item.quantity) || 1, handle: item.handle || ''}));
    return {
      items,
      productIds: toIds(items.map((item) => item.product_id)).sort((a, b) => a - b),
      variantIds: toIds(items.map((item) => item.variant_id)).sort((a, b) => a - b),
      handles: new Set(items.map((item) => item.handle).filter(Boolean)),
      subtotal: Number(raw.total_price) || 0,
      itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
      lineCount: items.length,
    };
  }

  const signature = (context) => `${context.productIds.join(',')}|${context.variantIds.join(',')}`;

  let cartPromise = null;
  function fetchCart() {
    if (!cartPromise) {
      cartPromise = fetch(`${CART_URL}.js`, {headers: {Accept: 'application/json'}})
        .then((response) => (response.ok ? response.json() : null))
        .then((cart) => {
          if (cart) vh.lastCart = cart;
          return cart;
        })
        .finally(() => {
          setTimeout(() => (cartPromise = null), 0);
        });
    }
    return cartPromise;
  }

  /* The cart before a change: what this page last saw, else an empty cart when it was empty at page load */
  function knownCart() {
    if (vh.lastCart) return cartContext(vh.lastCart);
    return window.theme && Number(window.theme.subtotal) === 0 ? cartContext({items: [], total_price: 0}) : null;
  }

  /* The product behind a "Læg i kurv" button: a card, a cart row, an offer or the product page */
  function productIdFrom(button, formData) {
    const holder = button && button.closest && button.closest('[data-quick-add-holder]');
    if (holder && Number(holder.dataset.quickAddHolder)) return Number(holder.dataset.quickAddHolder);
    const info = button && button.closest && button.closest('[data-product-id]');
    if (info && Number(info.dataset.productId)) return Number(info.dataset.productId);
    return Number(formData && formData.get && formData.get('product-id')) || 0;
  }

  /* The cart with one more product (at the tap on "Læg i kurv", or as a guess on its product page) */
  function withProduct(context, productId, variantId) {
    if (!context || !productId) return null;
    const items = context.items.concat({product_id: productId, variant_id: variantId || 0, quantity: 1, handle: ''});
    return cartContext({items, total_price: context.subtotal});
  }

  // The carts asked for ahead of a tap on this page (signature and visitor)
  const prefetched = new Set();

  /* A tap on "Læg i kurv" on its way: the pointer rests on the button (150 ms), a finger touches it or the keyboard is
     on it. The lists for the cart with that product (the variant its form would add) are asked for then, so they're at
     hand when the drawer opens. */
  let intentWatched = false;
  function warmOnIntent(owner) {
    if (intentWatched) return;
    intentWatched = true;
    const SELECTOR = '[data-add-to-cart]';
    const warm = (button) => {
      if (!button || button.disabled || !owner.isConnected) return;
      const form = button.form || button.closest('form');
      const known = knownCart();
      if (!form || !known) return;
      try {
        const formData = new FormData(form);
        owner.prefetch(withProduct(known, productIdFrom(button, formData), Number(formData.get('id'))), known);
      } catch (e) {
        /* a form FormData can't read: the tap asks */
      }
    };
    const target = (event) => (event.target && event.target.closest ? event.target.closest(SELECTOR) : null);
    let timer = 0;
    document.addEventListener(
      'pointerover',
      (event) => {
        if (event.pointerType && event.pointerType !== 'mouse') return;
        clearTimeout(timer);
        const button = target(event);
        if (button) timer = setTimeout(() => warm(button), 150);
      },
      {passive: true}
    );
    document.addEventListener('touchstart', (event) => warm(target(event)), {passive: true});
    document.addEventListener('focusin', (event) => warm(target(event)));
  }

  class VhRebuyCart extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.uid = `rb${(instances += 1)}`;
      this.widgets = String(this.dataset.widgets || '')
        .split(',')
        .map((id) => id.trim())
        .filter((id) => /^\d+$/.test(id));
      this.limit = Number(this.dataset.limit) || 4;
      this.inDrawer = this.dataset.placement === 'drawer';
      this.shownFor = null;

      this.onChange = () => {
        // During Broadcast's build Shopify's own render of the cart is at hand (vh.renderedCart), else it's fetched
        const rendered = typeof vh.renderedCart === 'function' ? vh.renderedCart() : null;
        if (this.inDrawer && !this.drawerOpen()) {
          this.stale = true;
          this.waiting = rendered;
          return;
        }
        this.refresh(rendered);
      };
      this.onOpen = () => {
        // Opened for an add: the tap draws the new cart's lists, and the cart change that follows checks them
        if (this.drawer() && this.drawer().classList.contains('vh-cart-adding')) return;
        // Lists drawn at a tap whose cart never came (the add failed) are checked against Shopify's cart
        if (this.stale || !this.shownFor || this.predicted) this.refresh(this.waiting);
      };
      this.onAdding = (event) => {
        const {formData, button} = event.detail || {};
        const next = withProduct(knownCart(), productIdFrom(button, formData), Number(formData && formData.get && formData.get('id')));
        if (!next) return;
        const sig = signature(next);
        if (sig === this.shownFor) return;
        log('asking Rebuy at the tap for', sig);
        this.tapped = {sig, at: Date.now()};
        this.lists(next).catch(() => {});
        // The add opens the drawer in this same task. Once it's open, the new cart's lists are drawn as soon as they
        // are ready, without waiting for Shopify's answer to the add.
        setTimeout(() => {
          if (this.tapped && this.tapped.sig === sig && (!this.inDrawer || this.drawerOpen())) this.show(next, (this.run = {}), true).catch(() => {});
        }, 0);
      };
      document.addEventListener('theme:cart:change', this.onChange);
      document.addEventListener('vh:cart:adding', this.onAdding);
      if (this.inDrawer) document.addEventListener('theme:cart-drawer:open', this.onOpen);

      if (!this.inDrawer || this.drawerOpen()) this.refresh();
      idle(() => this.warm());
      warmOnIntent(this);
    }

    disconnectedCallback() {
      document.removeEventListener('theme:cart:change', this.onChange);
      document.removeEventListener('vh:cart:adding', this.onAdding);
      document.removeEventListener('theme:cart-drawer:open', this.onOpen);
      this._ready = false;
    }

    drawer() {
      return this.closest('cart-drawer');
    }

    drawerOpen() {
      const drawer = this.drawer();
      return Boolean(drawer && drawer.classList.contains('is-open'));
    }

    /* When the page is idle: the widgets' settings, and on a product page the lists for the cart with this product, so
       an add doesn't wait for Rebuy's first (slow) answer to a new cart. Asked again once the visitor is known (Rebuy
       is asked with the visitor's id, which the tap will send) and when another variant is chosen. */
    async warm() {
      this.widgets.forEach((id) => widgetSettings(id).catch(() => {}));
      if (!this.inDrawer || !cfg.productId || !cfg.productAvailable) return;
      // The cart is usually known by now (assets/vh-cart.js checks it at page load); else it's fetched once
      let known = knownCart();
      if (!known) {
        await new Promise((resolve) => setTimeout(resolve, 1500));
        known = knownCart();
      }
      if (!known) {
        try {
          known = cartContext(await fetchCart());
        } catch (e) {
          return;
        }
      }
      // The variant the product form would add now (the theme preselects a case), as the tap on "Læg i kurv" will ask
      const form = document.querySelector('form[data-product-form]');
      const ask = () => {
        const field = form && form.elements.namedItem('id');
        const variantId = Number(field && field.value) || Number(cfg.variantId);
        const current = knownCart() || known;
        this.prefetch(withProduct(current, Number(cfg.productId), variantId), current);
      };
      ask();
      trackerReady.then(ask);
      // after the tracker's own handler, which sets the visitor
      document.addEventListener('visitorConsentCollected', () => setTimeout(ask, 0));
      if (form) form.addEventListener('change', () => setTimeout(ask, 0));
    }

    /* The lists for a cart that may come (not one that's already here), a few per page: Rebuy's answer and the cards
       are then at hand at the tap */
    prefetch(next, known) {
      if (!next || (known && next.productIds.length === known.productIds.length)) return; // already in the cart
      const key = `${signature(next)}|${tracker.uuid() || ''}`;
      if (prefetched.has(key) || prefetched.size >= PREFETCH_MAX) return;
      prefetched.add(key);
      log('asking Rebuy ahead for', signature(next));
      this.lists(next).catch(() => {});
    }

    /* Everything the lists need for a cart: Rebuy's products per widget and Shopify's cards. With `wait` (ms): the
       widgets that have answered by then, or a moment after the first one with products; without, all of them. */
    lists(context, wait) {
      const askFor = this.limit + 4;
      const exclude = {ids: new Set(context.productIds), handles: context.handles};
      const requests = this.widgets.map((widgetId) =>
        widgetRecommendations(widgetId, context, askFor).catch((error) => {
          log('widget failed', widgetId, error);
          return null;
        })
      );
      const useful = (result) => Boolean(result && result.products.some((product) => usable(product, exclude)));
      if (wait !== undefined) {
        // for the cart in front of the visitor: a widget's first cards are fetched as soon as it answers, while the
        // others are still waited for
        requests.forEach((request) =>
          request.then((result) => {
            if (result) result.products.filter((product) => usable(product, exclude)).slice(0, this.limit).forEach((product) => cardHtml(product.handle, CART_SECTION));
          })
        );
      }
      return (wait === undefined ? Promise.all(requests) : settle(requests, wait, WIDGET_GRACE, useful))
        .then((results) => {
          // Each widget up to its own limit (Rebuy's setting or a rule), then the first ones fill the room in the cart
          const lists = results.filter(Boolean).map((result) => ({...result, max: Math.min(this.limit, result.limit || this.limit)}));
          return planCards(lists, CART_SECTION, exclude, this.limit);
        })
        .then((planned) => {
          let room = this.limit;
          return planned.map((list) => {
            const items = list.items.slice(0, Math.max(0, room));
            room -= items.length;
            return {...list, items};
          });
        });
    }

    async refresh(rendered) {
      const run = (this.run = {});
      let context = rendered ? cartContext(rendered) : null;
      if (!context) {
        try {
          context = cartContext(await fetchCart());
        } catch (e) {
          context = null;
        }
      }
      if (run !== this.run || !context) return;
      this.stale = false;
      this.waiting = null;
      await this.show(context, run, false);
    }

    /* The lists for a cart: Shopify's, or at the tap the cart as it will be (`predicted`). Rebuy's first answer to a new
       cart takes 2–4 s, so the lists from the cart before stay (without what's now in the cart) until the new ones are
       in, up to 8 s; the placeholder (nothing to show from before) stays up to the cart wait. Both are counted from the
       tap when this is the cart it asked for. Then the theme's own list, rather than switching from it to Rebuy's; it
       follows Shopify's cart, so it's only drawn for that. */
    async show(context, run, predicted) {
      if (!context.productIds.length) {
        this.clear();
        return;
      }
      const sig = signature(context);
      if (sig === this.shownFor) {
        if (!predicted) this.predicted = false;
        return;
      }
      this.prune(context);
      if (this.hidden) this.loading();
      const limit = this.querySelector('.vh-upsell-skeleton') ? CART_WAIT : WIDGET_WAIT;

      const tap = this.tapped;
      const since = tap && tap.sig === sig && Date.now() - tap.at < limit ? tap.at : Date.now();
      let lists = [];
      try {
        lists = (await this.lists(context, limit - (Date.now() - since))).filter((list) => list.items.length);
      } catch (e) {
        lists = [];
      }
      if (run !== this.run) return;
      if (lists.length) {
        this.render(lists);
      } else if (predicted) {
        return; // the placeholder stays until Shopify's cart is here
      } else {
        await this.fallback(run);
      }
      if (run !== this.run) return;
      this.shownFor = sig;
      this.predicted = predicted;
      if (!predicted) this.tapped = null;
    }

    /* Products that are now in the cart leave the lists at once */
    prune(context) {
      this.querySelectorAll('[data-vh-rebuy-product]').forEach((el) => {
        if (context.productIds.includes(Number(el.dataset.vhRebuyProduct))) el.remove();
      });
      this.querySelectorAll('.vh-cart-upsells').forEach((group) => {
        if (!group.querySelector('.vh-cart-upsells__list > *')) group.remove();
      });
      if (!this.querySelector('.vh-cart-upsells')) this.hidden = true;
    }

    /* Two placeholder rows while Rebuy answers */
    loading() {
      const group = this.group(this.dataset.title, '<span class="vh-upsell-skeleton"></span><span class="vh-upsell-skeleton"></span>');
      group.setAttribute('aria-busy', 'true');
      this.replaceChildren(group);
      this.hidden = false;
    }

    clear() {
      this.replaceChildren();
      this.hidden = true;
      this.shownFor = null;
    }

    group(title, html) {
      const group = document.createElement('div');
      group.className = 'vh-cart-upsells';
      const heading = document.createElement('p');
      heading.className = 'vh-cart-upsells__title';
      heading.textContent = title || this.dataset.title || '';
      const list = document.createElement('div');
      list.className = 'vh-cart-upsells__list';
      list.innerHTML = html;
      group.append(heading, list);
      return group;
    }

    render(lists) {
      const groups = lists.map((list, index) => {
        const group = this.group(list.title, list.items.map((item) => uniqueIds(item.html, CART_SECTION, `${this.uid}-${index}`)).join(''));
        group.setAttribute('data-vh-rebuy', '');
        group.dataset.widgetId = list.widgetId;
        markList(group.querySelector('.vh-cart-upsells__list'), list.items, list.widgetId, ATTRIBUTION[list.type] || ATTRIBUTION.cart, list.tracking);
        return group;
      });
      this.replaceChildren(...groups);
      this.hidden = false;
      groups.forEach((group) => track.widgetView(group.dataset.widgetId));
    }

    /* The theme's own recommendations (Rebuy has none for this cart, or didn't answer in time) */
    async fallback(run) {
      let html = '';
      try {
        const response = await fetch(`${CART_URL}?section_id=${FALLBACK_SECTION}`);
        html = response.ok ? sectionInner(await response.text()) : '';
      } catch (e) {
        html = '';
      }
      if (run !== this.run) return;
      if (!html) {
        this.clear();
        return;
      }
      this.replaceChildren(this.group(this.dataset.title, uniqueIds(html, FALLBACK_SECTION, `${this.uid}-own`)));
      this.hidden = false;
    }
  }
  if (!customElements.get('vh-rebuy-cart')) customElements.define('vh-rebuy-cart', VhRebuyCart);

  /* ------------------------------------------------------------------------
     Section "Rebuy-anbefalinger" (product, collection, landing and home pages)
     ------------------------------------------------------------------------ */
  // Rebuy's own lists for a section without a widget ("Uden widget"). "viewed" needs Rebuy's visitor (consent),
  // "purchased" also a customer who is logged in.
  const SOURCES = {
    recommended: (context) => (context.productIds.length ? '/products/similar_products' : '/products/recommended'),
    top_sellers: () => '/products/top_sellers',
    viewed: () => '/products/viewed',
    purchased: () => '/products/purchased',
  };

  class VhRebuyRail extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.uid = `rb${(instances += 1)}`;
      if (!('IntersectionObserver' in window)) return this.load();
      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer.disconnect();
          this.load();
        },
        {rootMargin: '600px 0px'}
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    hideSection() {
      const section = this.closest('[data-vh-lazy-section]') || this;
      section.hidden = true;
    }

    /* Products already on the page (the collection's grid, rows above): left out when the section says so */
    shownOnPage() {
      const handles = new Set();
      document.querySelectorAll('main grid-item[data-product-handle]').forEach((card) => {
        if (!this.contains(card)) handles.add(card.getAttribute('data-product-handle'));
      });
      return handles;
    }

    async load() {
      const widgetId = (this.dataset.widgetId || '').trim();
      const source = SOURCES[this.dataset.source] ? this.dataset.source : 'recommended';
      const limit = Number(this.dataset.limit) || 8;
      // The page's product (product page) or collection (collection page), as Rebuy's widgets send them
      const context = {
        productIds: toIds([this.dataset.productId]),
        variantIds: toIds([this.dataset.variantId]),
        collectionIds: toIds([this.dataset.collectionId]),
      };
      const shown = this.dataset.excludeShown === 'true' ? this.shownOnPage() : new Set();
      const askFor = limit + 4 + Math.min(shown.size, 12);
      const exclude = {ids: new Set(context.productIds), handles: shown};
      if (this.dataset.exclude) exclude.handles.add(this.dataset.exclude);
      // Rebuy's widgets start once the page has loaded and consent is known. A widget (its rules may use the visitor)
      // or a personal list waits for that, up to 8 s; Rebuy's general lists wait at most 3 s.
      const personal = Boolean(widgetId) || source === 'viewed' || source === 'purchased';
      try {
        await withTimeout(trackerReady, personal ? 8000 : 3000);
        let result;
        if (widgetId) {
          result = await widgetRecommendations(widgetId, context, askFor);
        } else {
          // A personal list without its visitor (no consent, not logged in) has nothing to show: no request
          const uuid = tracker.uuid();
          if ((source === 'viewed' && !uuid) || (source === 'purchased' && !(uuid && cfg.customerId))) return this.hideSection();
          result = {widgetId: '', type: 'product', title: '', limit: 0, ...(await recommend(SOURCES[source](context), context, askFor))};
        }
        const max = Math.min(limit, result.limit || limit);
        const [list] = await planCards([{...result, max}], CARD_SECTION, exclude);
        if (!list.items.length) return this.hideSection();
        this.render(list);
      } catch (error) {
        log('Rebuy section failed', error);
        this.hideSection();
      }
    }

    render(list) {
      const template = this.querySelector('template[data-vh-rebuy-slider]');
      if (!template) return this.hideSection();
      const slider = template.content.firstElementChild.cloneNode(true);
      const railTrack = slider.querySelector('[data-vh-slider-track]');
      railTrack.innerHTML = list.items.map((item) => uniqueIds(item.html, CARD_SECTION, `vh-rebuy-${this.uid}`)).join('');
      slider.setAttribute('data-vh-rebuy', '');
      slider.dataset.widgetId = list.widgetId || '';
      markList(railTrack, list.items, list.widgetId, ATTRIBUTION[list.type] || ATTRIBUTION.product, list.tracking);

      if (this.dataset.rebuyTitle === 'true' && list.title) {
        const section = this.closest('section');
        const heading = section && section.querySelector('[data-vh-rebuy-title]');
        if (heading) heading.textContent = list.title;
      }
      Array.from(this.children).forEach((child) => {
        if (child !== template) child.remove();
      });
      this.appendChild(slider);
      if (typeof vh.updateWishlistButtons === 'function') vh.updateWishlistButtons(this);
      track.widgetView(list.widgetId);
    }
  }
  if (!customElements.get('vh-rebuy-rail')) customElements.define('vh-rebuy-rail', VhRebuyRail);

  vh.rebuy = {recommend, widgetRecommendations, track, userCanBeTracked};
})();
